import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { StickyNote, SectionLabel } from "@/components/Notebook";
import { Tick } from "@/components/Annotation";
import { SOCIAL } from "@/config/links";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";
import { capture, getGaClientId, getAttributionContext, trackFormStarted, trackFormSubmitAttempted, trackFormSubmissionFailed, generateFormAttemptId, trackAssetViewed, buildAttributionRequestContext, captureOaiqLeadCreated } from "@/lib/analytics";
import { getAnalyticsConsentState } from "@/hooks/useConsent";

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  { value: "High-Stakes Presentation Coaching", labelKey: "contact.svc_high_stakes" },
  { value: "Executive Presence Development", labelKey: "contact.svc_epd" },
  { value: "Corporate Training", labelKey: "contact.svc_corporate" },
  { value: "L&D Partnership", labelKey: "contact.svc_ld" },
  { value: "Something else", labelKey: "contact.svc_other" },
];

const emptyForm = {
  full_name: "",
  company: "",
  role: "",
  email: "",
  country: "",
  phone: "",
  service: "",
  message: "",
  consent: false,
};

export default function Contact() {
  const { t, lang, l } = useLang();
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState({ state: "idle", error: null }); // idle | submitting | success | error
  const [gaClientId, setGaClientId] = useState(null);
  const formStartedRef = useRef(false);
  // Persists for the ENTIRE lifetime of this form-filling attempt —
  // generated ONCE, at the first real field mutation below, and
  // reused (never regenerated) by every form_started/form_submit_
  // attempted/form_submission_failed call for this session. Distinct
  // from the FRESH per-submit-click `attemptId` generated inside
  // `submit()` below.
  const contactFormAttemptIdRef = useRef(null);
  const attribution = getAttributionContext() || {};
  // InterAct Tracking Foundation milestone (2026-10-10) — captures the
  // CTA that led here (if any), one-shot, at mount — see
  // `trackAssetViewed()`'s own docstring for why this must happen
  // here, not re-read later at submit time.
  const ctaNavContextRef = useRef(null);

  useEffect(() => {
    getGaClientId().then(setGaClientId);
    ctaNavContextRef.current = trackAssetViewed("contact_request", { placement: "full_page" });
  }, []);

  const update = (field) => (e) => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      contactFormAttemptIdRef.current = generateFormAttemptId();
      trackFormStarted({ formId: "contact_form", assetId: "contact_request", formAttemptId: contactFormAttemptIdRef.current });
    }
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", error: null });
    // Fresh per-submit-click id — distinct from the persistent
    // `contactFormAttemptIdRef` above. Reused (not regenerated) by
    // the corresponding failure call below for THIS same click.
    const attemptId = generateFormAttemptId();
    trackFormSubmitAttempted({ formId: "contact_form", assetId: "contact_request", formAttemptId: contactFormAttemptIdRef.current, attemptId });
    try {
      const attribution = getAttributionContext() || {};
      const gaClientId = await getGaClientId();
      const res = await axios.post(`${API}/api/leads/contact`, {
        ...form,
        source_url: window.location.href,
        locale: lang,
        ga_client_id: gaClientId || undefined,
        gclid: attribution.gclid || undefined,
        oppref: attribution.oppref || undefined,
        analytics_consent: getAnalyticsConsentState(),
        attribution_context: buildAttributionRequestContext({ ctaNavContext: ctaNavContextRef.current }),
      });
      setStatus({ state: "success", error: null });
      capture("form_submitted", {
        form_id: "contact_form",
        page_path: window.location.pathname,
        form_type: "contact",
        service: form.service || null,
        language: lang,
      });
      // Lead-created tracking audit (2026-02) — a real email form,
      // successfully accepted/persisted by the backend (the textbook
      // definition of a lead). `submission_id` is the backend's own
      // idempotency key for this submission (now returned in the
      // response), reused unchanged as the OpenAI Pixel's official
      // `event_id` dedup key.
      captureOaiqLeadCreated(res.data?.submission_id);
      setForm(emptyForm);
    } catch (err) {
      const errorCode = !err.response ? "network_error" : err.response.status >= 500 ? "server_error" : err.response.status === 429 ? "rate_limited" : "validation_error";
      trackFormSubmissionFailed({ formId: "contact_form", assetId: "contact_request", formAttemptId: contactFormAttemptIdRef.current, attemptId, errorCode });
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => (typeof d?.msg === "string" ? d.msg : JSON.stringify(d))).join("; ")
        : typeof detail === "string"
          ? detail
          : t("form.generic_error");
      setStatus({
        state: "error",
        error: message,
      });
    }
  };

  const canSubmit =
    form.full_name && form.email && form.role && form.country && form.service && form.message && form.consent;

  return (
    <>
      <PageMeta
        titleKey="meta.contact_title"
        descriptionKey="meta.contact_description"
        path={ROUTES.contact}
      />
      <PageHero
        testId="contact-hero"
        eyebrow={t("contact.hero_eyebrow")}
        headline={[t("contact.hero_h1")]}
        emphasis={t("contact.hero_emphasis")}
        emphasisAccent="underline"
        intro={t("contact.hero_intro")}
      />

      <section className="relative z-10 pb-24 md:pb-32" data-testid="contact-body">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-4 order-2 lg:order-1">
            <SectionLabel className="mb-5 block">{t("contact.label_handle")}</SectionLabel>
            <ul className="space-y-4 mb-8">
              {["contact.trust_1", "contact.trust_2", "contact.trust_3"].map((k) => (
                <li key={k} className="flex items-start gap-3">
                  <Tick className="w-4 h-4 mt-1 shrink-0" />
                  <span className="font-body text-ink leading-relaxed">{t(k)}</span>
                </li>
              ))}
            </ul>

            <SectionLabel className="mb-4 block">{t("contact.label_elsewhere")}</SectionLabel>
            <ul className="space-y-2 font-body text-ink-soft">
              <li><a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta">LinkedIn</a></li>
              <li><a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta">Instagram</a></li>
              <li><a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta">Facebook</a></li>
              <li className="pt-3"><a href="mailto:christos@interactglobally.com.br" className="hover:text-terracotta">christos@interactglobally.com.br</a></li>
            </ul>

            <StickyNote color="#E7DDC8" rotate={-3} className="inline-block mt-8">
              {t("contact.sticky_note")}
            </StickyNote>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-8 order-1 lg:order-2">
            {status.state === "success" ? (
              <div
                data-testid="contact-form-success"
                className="bg-paper border border-ink/15 shadow-[6px_12px_44px_rgba(27,27,24,0.08)] p-8 md:p-10 text-center"
              >
                <p className="font-hand text-terracotta text-2xl mb-4">{t("form.success_hand")}</p>
                <h2 className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                  {t("form.success_h")}
                </h2>
                <p className="mt-5 font-body text-ink-soft leading-relaxed max-w-xl mx-auto">
                  {t("form.success_body_a")}
                  <a
                    href={l("/insights")}
                    className="underline decoration-terracotta/60 hover:text-terracotta"
                  >
                    {t("form.success_body_link1")}
                  </a>
                  {t("form.success_body_or")}
                  <a
                    href={l("/booking")}
                    className="underline decoration-terracotta/60 hover:text-terracotta"
                  >
                    {t("form.success_body_link2")}
                  </a>
                  {t("form.success_body_c")}
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-paper border border-ink/15 shadow-[6px_12px_44px_rgba(27,27,24,0.08)] p-6 md:p-10 grid gap-5"
                noValidate
              >
                <input type="hidden" name="ga_client_id" value={gaClientId || ""} data-testid="contact-form-ga-client-id" />
                <input type="hidden" name="gclid" value={attribution.gclid || ""} data-testid="contact-form-gclid" />
                <input type="hidden" name="oppref" value={attribution.oppref || ""} data-testid="contact-form-oppref" />
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label={t("contact.field_full_name")} required>
                    <input required value={form.full_name} onChange={update("full_name")} data-testid="contact-full-name" name="full_name" autoComplete="name" className={inputCls} />
                  </Field>
                  <Field label={t("contact.field_company")} hint={t("contact.hint_optional")}>
                    <input value={form.company} onChange={update("company")} data-testid="contact-company" name="company" autoComplete="organization" className={inputCls} />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label={t("contact.field_role")} required>
                    <input required value={form.role} onChange={update("role")} data-testid="contact-role" name="role" autoComplete="organization-title" className={inputCls} />
                  </Field>
                  <Field label={t("contact.field_email")} required>
                    <input required type="email" value={form.email} onChange={update("email")} data-testid="contact-email" name="email" autoComplete="email" className={inputCls} />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label={t("contact.field_country")} required>
                    <input required value={form.country} onChange={update("country")} data-testid="contact-country" name="country" autoComplete="country-name" className={inputCls} />
                  </Field>
                  <Field label={t("contact.field_phone")} hint={t("contact.hint_optional")}>
                    <input type="tel" value={form.phone} onChange={update("phone")} data-testid="contact-phone" name="phone" autoComplete="tel" className={inputCls} />
                  </Field>
                </div>

                <Field label={t("contact.field_service")} required>
                  <select required value={form.service} onChange={update("service")} data-testid="contact-service" name="service" className={inputCls}>
                    <option value="" disabled>{t("contact.select_placeholder")}</option>
                    {SERVICES.map((s) => (
                      <option key={s.value} value={s.value}>{t(s.labelKey)}</option>
                    ))}
                  </select>
                </Field>

                <Field label={t("contact.field_message")} required>
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-message"
                    name="message"
                    placeholder={t("contact.message_placeholder")}
                    className={`${inputCls} resize-none`}
                  />
                </Field>

                <label className="flex items-start gap-3 pt-2 cursor-pointer" data-testid="contact-consent-label">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={update("consent")}
                    data-testid="contact-consent"
                    className="mt-1 w-5 h-5 accent-mustard shrink-0"
                    required
                  />
                  <span className="font-body text-sm text-ink-soft leading-relaxed">
                    {t("contact.consent_a")}
                    <a href={l("/privacy")} className="underline decoration-terracotta/60 hover:text-terracotta">
                      {t("contact.consent_link")}
                    </a>
                    {t("contact.consent_c")}
                  </span>
                </label>

                {status.state === "error" && (
                  <p role="alert" data-testid="contact-form-error" className="font-body text-sm text-terracotta bg-terracotta/10 border border-terracotta/25 p-3">
                    {status.error}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={!canSubmit || status.state === "submitting"}
                    data-testid="contact-submit"
                    className="bg-ink text-paper px-8 py-4 font-body text-xs uppercase tracking-[0.22em] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status.state === "submitting" ? t("form.sending") : t("form.send")}
                  </button>
                  <span className="font-hand text-ink-soft text-base">
                    {t("form.typical_reply")}
                  </span>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full bg-paper-deep/30 border border-ink/20 focus:border-terracotta focus:outline-none px-4 py-3 font-body text-ink placeholder:text-ink-soft/60 transition-colors";

const Field = ({ label, hint, required, children }) => (
  <label className="block">
    <span className="font-body text-xs uppercase tracking-[0.18em] text-ink-soft mb-2 flex items-center gap-2">
      {label}
      {required && <span className="text-terracotta">*</span>}
      {hint && <span className="font-hand text-pencil text-sm normal-case tracking-normal">{hint}</span>}
    </span>
    {children}
  </label>
);
